import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowRight, LogIn } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

/** Abstract WeCare II figure mark — used only as a subtle branded visual */
function WcMark({ className = "", color = "#ffffff", size = 260, opacity = 1 }) {
  return (
    <svg
      viewBox="0 0 60 90"
      width={size}
      height={size * 1.5}
      className={className}
      fill="none"
      style={{ opacity }}
      aria-hidden="true"
    >
      <circle cx="30" cy="12" r="8" fill={color} />
      <path
        d="M30 24 C 44 24, 50 36, 46 54 C 44 66, 38 76, 30 84 C 22 76, 16 66, 14 54 C 10 36, 16 24, 30 24 Z"
        fill={color}
      />
    </svg>
  );
}

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const routeFor = (role) =>
    role === "admin" ? "/admin" : role === "employee" ? "/academy" : role === "family" ? "/family" : "/";

  useEffect(() => {
    if (user && typeof user === "object") nav(routeFor(user.role), { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav(loc.state?.from || routeFor(res.user.role), { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <>
      <Toaster richColors position="top-right" />
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-brand-bone" data-testid="login-page">
        {/* LEFT — navy branded panel */}
        <div className="hidden lg:block relative overflow-hidden bg-brand-navy">
          {/* Subtle tonal gradient */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(120% 80% at 100% 0%, rgba(243,147,37,0.10), transparent 55%), radial-gradient(80% 60% at 0% 100%, rgba(255,255,255,0.04), transparent 60%)",
            }}
            aria-hidden="true"
          />
          {/* Oversized WeCare II figure watermark — cropped at lower-right, extremely low opacity */}
          <WcMark
            className="absolute -right-24 -bottom-24 xl:-right-32 xl:-bottom-32"
            color="#ffffff"
            size={520}
            opacity={0.06}
          />

          <div className="relative h-full flex flex-col p-12 xl:p-16 text-white">
            {/* Logo — reversed/knockout: white wordmark + orange figure, no container */}
            <Link
              to="/"
              data-testid="login-brand-link"
              className="inline-flex items-center gap-2 w-fit"
              aria-label="WeCare II"
            >
              <span className="font-outfit font-bold text-white text-[38px] tracking-tight leading-none">
                wecare
              </span>
              <WcMark color="#f39325" size={32} opacity={1} />
            </Link>

            {/* Messaging — vertically balanced, sits in the upper-middle */}
            <div className="mt-16 xl:mt-20 max-w-md">
              <div className="inline-flex items-center gap-2.5 text-[10px] xl:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
                <span className="h-px w-8 bg-brand-orange" />
                Team Access
              </div>
              <h2 className="mt-5 font-outfit font-medium tracking-tight leading-[1.08] text-white text-[38px] xl:text-[46px]">
                A home for the WeCare II{" "}
                <span
                  className="italic text-brand-orange"
                  style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
                >
                  team
                </span>
                .
              </h2>
              <p className="mt-6 text-white/80 text-[15px] leading-relaxed max-w-sm">
                Secure access for authorized WeCare II employees and administrators.
              </p>
            </div>

            {/* Small footer inside the navy panel */}
            <div className="mt-auto text-[11px] uppercase tracking-[0.22em] text-white/40 font-semibold">
              WeCare II · Private Workspace
            </div>
          </div>
        </div>

        {/* RIGHT — clean sign-in */}
        <div className="flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Mobile-only compact logo */}
            <div className="lg:hidden mb-8">
              <Link to="/" data-testid="login-brand-mobile">
                <BrandLogo className="h-10" />
              </Link>
            </div>

            <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-orange">
              <span className="h-px w-8 bg-brand-orange" />
              Sign In
            </div>
            <h1 className="mt-4 font-outfit text-4xl md:text-[42px] font-medium tracking-tight text-brand-navy leading-[1.1]">
              Welcome back.
            </h1>
            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
              Sign in with your WeCare II employee credentials.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-navy">Email</label>
                <input
                  required
                  type="email"
                  autoComplete="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@wecareii.com"
                  className="mt-2 w-full rounded-lg bg-white border border-brand-stone/70 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-navy">Password</label>
                  <a
                    href="mailto:info@wecareii.com?subject=Password%20reset%20request"
                    className="text-[11px] font-semibold text-brand-orange hover:brightness-90"
                    data-testid="login-forgot-link"
                  >
                    Forgot password?
                  </a>
                </div>
                <input
                  required
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-2 w-full rounded-lg bg-white border border-brand-stone/70 px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white hover:bg-brand-navydark disabled:opacity-60 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
                style={{ transition: "background-color 200ms ease" }}
              >
                {submitting ? "Signing in…" : (<><LogIn className="w-4 h-4" /> Sign In <ArrowRight className="w-3.5 h-3.5" /></>)}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
